<script setup lang="ts">
import { computed, onMounted, ref, type Component } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/common/BaseButton.vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import PageHeader from '@/components/layout/PageHeader.vue'
import AppTabbar from '@/components/common/AppTabbar.vue'
import BountyBanner from '@/components/home/BountyBanner.vue'
import EscrowPreviewCard from '@/components/home/EscrowPreviewCard.vue'
import ListingCard from '@/components/listings/ListingCard.vue'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useListingsStore } from '@/stores/listings'
import type { ListingSummary } from '@/types/api'
import {
  PhArrowRight,
  PhBriefcase,
  PhCamera,
  PhChatsCircle,
  PhCode,
  PhCompass,
  PhLightning,
  PhLock,
  PhMegaphone,
  PhMusicNotes,
  PhPalette,
  PhPenNib,
  PhShieldCheck,
  PhSparkle,
  PhStorefront,
  PhVideoCamera,
  PhXLogo,
} from '@phosphor-icons/vue'
import { X_PROFILE_URL } from '@/config/social'
import { isBountyLive } from '@/config/bounty'
import logoCutout from '@/assets/images/logo-infinity-cutout.png'

// The introduction to the marketplace, for guests and signed-in users alike.
// Its job is to make people want to sell: the main actions lead to a new listing.

const router = useRouter()
const authStore = useAuthStore()
const listingsStore = useListingsStore()

// Real services show the market is alive and what work goes for in Pi.
// Fetched directly: the store's listings belong to Browse and its filters.
const recentListings = ref<ListingSummary[]>([])

async function fetchRecentListings() {
  try {
    const response = await api.getListings({ sort: 'newest', limit: 3 })
    recentListings.value = (response.data ?? []).slice(0, 3)
  } catch {
    // The intro reads fine without it; the row just stays hidden.
  }
}

onMounted(() => {
  void listingsStore.fetchCategories()
  void fetchRecentListings()
})

// Icons stored on a category, as Phosphor kebab names.
const iconsByName: Record<string, Component> = {
  palette: PhPalette,
  'music-notes': PhMusicNotes,
  code: PhCode,
  camera: PhCamera,
}

// Half the seeded categories have no icon, so the default slugs get one here.
const iconsBySlug: Record<string, Component> = {
  writing: PhPenNib,
  video: PhVideoCamera,
  marketing: PhMegaphone,
  business: PhBriefcase,
}

const featuredCategories = computed(() =>
  listingsStore.categories
    .filter(c => !c.parentID)
    .slice(0, 8)
    .map(category => ({
      ...category,
      iconComponent: (category.icon && iconsByName[category.icon]) || iconsBySlug[category.slug] || PhSparkle,
    })),
)

const sellingSteps = [
  { title: 'List your skill', body: 'Describe what you do, set your price in Pi and how fast you deliver.' },
  { title: 'Get booked', body: 'A buyer orders and pays. Their Pi is held safely in escrow from that moment.' },
  { title: 'Deliver & get paid', body: 'Hand over your work in the order chat. Once it is accepted, the Pi is yours.' },
]

const highlights = [
  { icon: PhLock, iconClass: 'text-accent-cyan', title: 'Escrow protected', body: 'Payment is secured before you start, so you never work for free.' },
  { icon: PhLightning, iconClass: 'text-accent-yellow', title: 'Instant settlement', body: 'Pi moves peer to peer on Pi Network, no bank in between.' },
  { icon: PhShieldCheck, iconClass: 'text-accent-green', title: 'Dispute resolution', body: 'Something went wrong? A moderator looks at both sides.' },
  { icon: PhChatsCircle, iconClass: 'text-accent-purple', title: 'Built-in order chat', body: 'Agree on details, share files and deliver, all in one thread.' },
  { icon: PhStorefront, iconClass: 'text-accent-blue', title: 'Your own storefront', body: 'One link to all your services, ready to share with your network.' },
  { icon: PhSparkle, iconClass: 'text-accent-orange', title: 'Early mover advantage', body: 'The market is young: the first sellers in a category get noticed.' },
]

// Checked once per visit: the banner disappears on the first load after the bounty ends.
const showBountyBanner = isBountyLive()

// Guests are sent to Login first; the route guard keeps the redirect to the form.
function startSelling() {
  void router.push('/create-listing/')
}

function browse(slug?: string) {
  void router.push(slug ? { path: '/browse/', query: { category: slug } } : '/browse/')
}
</script>

<template>
  <PageLayout nav="home">
    <template v-if="showBountyBanner" #banner>
      <BountyBanner />
    </template>
    <template #navbar>
      <PageHeader transparent>
        <template #left>
          <img :src="logoCutout" alt="TalentSwap" class="h-8 w-8 sm:h-10 sm:w-10" />
        </template>
        <template #right>
          <a
            :href="X_PROFILE_URL"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TalentSwap on X"
            class="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface/50 text-ink backdrop-blur-md transition-colors hover:border-line-3 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan/60"
          >
            <PhXLogo class="h-5 w-5" weight="bold" />
          </a>
        </template>
      </PageHeader>
    </template>

    <!-- Clips the ambient glows, which would otherwise scroll the page sideways. -->
    <div class="relative overflow-hidden">
      <div class="glow-cyan pointer-events-none absolute left-[-10%] top-[-5%] z-0 h-[600px] w-[70%]" aria-hidden="true" />
      <div class="glow-purple pointer-events-none absolute right-[-10%] top-[500px] z-0 h-[600px] w-[70%]" aria-hidden="true" />

      <div class="relative z-10 mx-auto w-full max-w-5xl px-6 pb-12 pt-6 md:px-8 md:pt-16">
        <!-- Hero -->
        <section class="lg:flex lg:items-center lg:justify-between lg:gap-12">
          <div class="md:max-w-2xl">
            <div
              class="mb-8 inline-flex w-max items-center gap-2 rounded-full border border-line-info bg-surface-info px-3 py-1.5 shadow-[0_0_15px_rgba(37,227,233,0.15)]"
            >
              <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-cyan" aria-hidden="true" />
              <span class="text-[10px] font-bold uppercase tracking-widest text-accent-cyan-soft">Now on Pi Testnet</span>
            </div>

            <h1 class="mb-5 text-[40px] font-extrabold leading-[1.1] tracking-tight text-ink md:text-6xl">
              Turn what you're <br />good at into
              <span class="bg-linear-to-r from-accent-cyan to-accent-blue bg-clip-text text-transparent">Pi</span>
            </h1>

            <p class="mb-10 pr-2 text-base leading-relaxed text-ink-5 md:text-lg">
              TalentSwap is the marketplace for services on Pi Network. Design a logo, mix a track,
              fix some code, teach a lesson: Pioneers everywhere pay in Pi, protected by escrow.
            </p>

            <div class="flex flex-col gap-3.5 sm:flex-row">
              <BaseButton variant="primary" size="lg" full-width class="sm:w-auto" @click="startSelling">
                Start selling
                <PhArrowRight class="h-5 w-5" weight="bold" />
              </BaseButton>
              <BaseButton
                variant="secondary"
                size="lg"
                full-width
                class="bg-surface/80 text-ink sm:w-auto"
                @click="browse()"
              >
                Browse services
                <PhCompass class="h-5 w-5 text-accent-cyan" />
              </BaseButton>
            </div>

            <p v-if="!authStore.isAuthenticated" class="mt-4 text-sm text-ink-5">
              Already a Pioneer?
              <RouterLink to="/login/" class="font-semibold text-accent-cyan hover:underline">Sign in with Pi</RouterLink>
            </p>
          </div>

          <!-- Fills the right of the hero on wide screens; phones keep the copy alone. -->
          <EscrowPreviewCard class="hidden shrink-0 lg:block" />
        </section>

        <!-- Categories -->
        <section v-if="featuredCategories.length" class="mt-16" aria-labelledby="home-categories">
          <h2 id="home-categories" class="mb-1 text-xl font-bold text-ink md:text-2xl">What will you offer?</h2>
          <p class="mb-5 text-sm text-ink-5">Pioneers are already looking for help in these categories.</p>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button
              v-for="category in featuredCategories"
              :key="category.id"
              type="button"
              class="flex cursor-pointer items-center gap-3 rounded-2xl border border-line bg-surface/60 p-4 text-left backdrop-blur-md transition-colors hover:border-line-3 hover:bg-surface-2"
              @click="browse(category.slug)"
            >
              <component :is="category.iconComponent" class="h-6 w-6 shrink-0 text-accent-cyan" />
              <span class="text-sm font-semibold text-ink">{{ category.name }}</span>
            </button>
          </div>
        </section>

        <!-- Recent listings -->
        <section v-if="recentListings.length" class="mt-16" aria-labelledby="home-recent">
          <div class="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 id="home-recent" class="mb-1 text-xl font-bold text-ink md:text-2xl">Fresh on TalentSwap</h2>
              <p class="text-sm text-ink-5">Real services, priced in Pi. Yours could be next.</p>
            </div>
            <RouterLink to="/browse/" class="shrink-0 text-sm font-semibold text-accent-cyan hover:underline">
              See all
            </RouterLink>
          </div>
          <!-- Phones swipe through the cards; md+ lays them out side by side. -->
          <div class="-mx-6 flex snap-x snap-mandatory scroll-px-6 gap-4 overflow-x-auto px-6 pb-2 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0">
            <div v-for="listing in recentListings" :key="listing.id" class="w-[78%] shrink-0 snap-start md:w-auto">
              <ListingCard :listing="listing" />
            </div>
          </div>
        </section>

        <!-- How selling works -->
        <section class="mt-16" aria-labelledby="home-steps">
          <h2 id="home-steps" class="mb-1 text-xl font-bold text-ink md:text-2xl">Selling in three steps</h2>
          <p class="mb-5 text-sm text-ink-5">From idea to income without leaving the app.</p>
          <ol class="grid gap-3 md:grid-cols-3">
            <li
              v-for="(step, i) in sellingSteps"
              :key="step.title"
              class="rounded-2xl border border-line bg-surface/60 p-5 backdrop-blur-md"
            >
              <span class="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-cta text-sm font-bold text-cta-foreground">
                {{ i + 1 }}
              </span>
              <h3 class="mb-1 font-semibold text-ink">{{ step.title }}</h3>
              <p class="text-sm leading-relaxed text-ink-5">{{ step.body }}</p>
            </li>
          </ol>
        </section>

        <!-- Why TalentSwap -->
        <section class="mt-16" aria-labelledby="home-highlights">
          <h2 id="home-highlights" class="mb-1 text-xl font-bold text-ink md:text-2xl">Built so you can sell with confidence</h2>
          <p class="mb-5 text-sm text-ink-5">Everything a freelancer needs, nothing to set up.</p>
          <div class="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div
              v-for="highlight in highlights"
              :key="highlight.title"
              class="rounded-2xl border border-line bg-surface/60 p-5 backdrop-blur-md"
            >
              <component :is="highlight.icon" class="mb-3 h-6 w-6" :class="highlight.iconClass" />
              <h3 class="mb-1 font-semibold text-ink">{{ highlight.title }}</h3>
              <p class="text-sm leading-relaxed text-ink-5">{{ highlight.body }}</p>
            </div>
          </div>
        </section>

        <!-- Closing call to action -->
        <section
          class="mt-16 rounded-3xl border border-line bg-surface-2 p-6 text-center md:p-10"
          aria-labelledby="home-cta"
        >
          <h2 id="home-cta" class="mb-2 text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
            Your skills are worth Pi.
          </h2>
          <p class="mx-auto mb-6 max-w-md text-sm leading-relaxed text-ink-5 md:text-base">
            A listing takes a few minutes. Put yours up today and be there when the next Pioneer comes looking.
          </p>
          <div class="flex flex-col justify-center gap-3 sm:flex-row">
            <BaseButton variant="primary" size="lg" @click="startSelling">
              <span class="inline-flex items-center justify-center gap-2">
                Create a listing
                <PhArrowRight class="h-5 w-5" weight="bold" />
              </span>
            </BaseButton>
            <!-- Buyers who scrolled this far get a way in too. -->
            <BaseButton variant="secondary" size="lg" class="bg-surface/80 text-ink" @click="browse()">
              <span class="inline-flex items-center justify-center gap-2">
                Hire a Pioneer
                <PhCompass class="h-5 w-5 text-accent-cyan" />
              </span>
            </BaseButton>
          </div>
        </section>

        <footer class="mt-12 flex items-center justify-between border-t border-line pt-6 text-sm text-ink-5">
          <span>© TalentSwap</span>
          <!-- Phones already have this link in the header, which desktop swaps for the top nav. -->
          <a
            :href="X_PROFILE_URL"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TalentSwap on X"
            class="hidden items-center gap-2 transition-colors hover:text-ink md:inline-flex"
          >
            <PhXLogo class="h-4 w-4" weight="bold" />
            Follow us
          </a>
        </footer>
      </div>
    </div>

    <template #fixed>
      <AppTabbar v-if="authStore.isAuthenticated" active="home" />
    </template>
  </PageLayout>
</template>
